export const AUTH_BOUNDARY_UNSETTLED_ERROR_CODE = 'auth_boundary_unsettled';

export interface ProtectedTransportSnapshot {
  readonly userId: string;
  readonly generation: number;
  readonly signal: AbortSignal;
}

type BoundaryPhase = 'uninitialized' | 'transitioning' | 'active' | 'signed_out';

export const createAuthBoundaryUnsettledError = (): Error & {
  code: string;
  retryable: boolean;
} => Object.assign(
  new Error('Authentication is changing. Please retry this request.'),
  {
    code: AUTH_BOUNDARY_UNSETTLED_ERROR_CODE,
    retryable: true,
  },
);

class ProtectedTransportBoundary {
  private generation = 0;
  private phase: BoundaryPhase = 'uninitialized';
  private activeUserId: string | null = null;
  private pendingUserId: string | null = null;
  private canonicalUserIdReader: (() => string | null) | null = null;
  private controller = new AbortController();
  private readonly objectUrls = new Set<string>();

  private revokeObjectUrl(url: string): void {
    if (!this.objectUrls.delete(url)) {
      return;
    }
    try {
      URL.revokeObjectURL(url);
    } catch {
      // URL revocation is best effort in older/test browser runtimes. The
      // generation fence still prevents a stale URL from being published.
    }
  }

  private rotateGeneration(): void {
    this.controller.abort();
    this.controller = new AbortController();
    this.generation += 1;
    Array.from(this.objectUrls).forEach((url) => {
      this.revokeObjectUrl(url);
    });
  }

  beginIdentityTransition(nextUserId: string | null): void {
    this.rotateGeneration();
    this.phase = 'transitioning';
    this.activeUserId = null;
    this.pendingUserId = nextUserId;
  }

  activateIdentity(
    userId: string | null,
    canonicalUserIdReader?: () => string | null,
  ): void {
    if (canonicalUserIdReader) {
      this.canonicalUserIdReader = canonicalUserIdReader;
    }
    if (
      this.phase === 'active'
      && userId !== null
      && this.activeUserId === userId
    ) {
      // Firebase emits same-user events for token and claim refreshes. Those
      // events must not interrupt an already authorized binary transfer.
      return;
    }
    if (this.phase === 'signed_out' && userId === null) {
      return;
    }

    const commitsAnnouncedTransition = (
      this.phase === 'transitioning'
      && this.pendingUserId === userId
    );
    if (!commitsAnnouncedTransition) {
      // Initial auth publication and any unexpected scope mismatch still
      // invalidate work that may have been created under an older provider.
      this.rotateGeneration();
    }

    this.pendingUserId = null;
    this.activeUserId = userId;
    this.phase = userId === null ? 'signed_out' : 'active';
  }

  cancelCurrentIdentityRequests(): void {
    // Logout is an intent boundary even before Firebase publishes the
    // signed-out event. Keep the current identity usable if signOut itself
    // fails, but make every already-dispatched binary response stale.
    this.rotateGeneration();
  }

  deactivate(): void {
    this.rotateGeneration();
    this.phase = 'uninitialized';
    this.activeUserId = null;
    this.pendingUserId = null;
    this.canonicalUserIdReader = null;
  }

  private canonicalIdentityMatches(userId: string): boolean {
    if (!this.canonicalUserIdReader) {
      return true;
    }
    try {
      return this.canonicalUserIdReader() === userId;
    } catch {
      return false;
    }
  }

  capture(): ProtectedTransportSnapshot {
    if (
      this.phase !== 'active'
      || !this.activeUserId
      || !this.canonicalIdentityMatches(this.activeUserId)
    ) {
      throw createAuthBoundaryUnsettledError();
    }
    return {
      userId: this.activeUserId,
      generation: this.generation,
      signal: this.controller.signal,
    };
  }

  isCurrent(snapshot: ProtectedTransportSnapshot): boolean {
    return (
      this.phase === 'active'
      && !snapshot.signal.aborted
      && snapshot.generation === this.generation
      && snapshot.userId === this.activeUserId
      && this.canonicalIdentityMatches(snapshot.userId)
    );
  }

  assertCurrent(snapshot: ProtectedTransportSnapshot): void {
    if (!this.isCurrent(snapshot)) {
      throw createAuthBoundaryUnsettledError();
    }
  }

  rethrowRequestError(
    snapshot: ProtectedTransportSnapshot,
    error: unknown,
  ): never {
    if (!this.isCurrent(snapshot)) {
      throw createAuthBoundaryUnsettledError();
    }
    throw error;
  }

  createObjectUrl(
    snapshot: ProtectedTransportSnapshot,
    blob: Blob,
  ): string {
    this.assertCurrent(snapshot);
    const url = URL.createObjectURL(blob);
    if (!this.isCurrent(snapshot)) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // The stale URL was never returned or attached to the document.
      }
      throw createAuthBoundaryUnsettledError();
    }
    this.objectUrls.add(url);
    return url;
  }

  releaseObjectUrl(url: string): void {
    this.revokeObjectUrl(url);
  }
}

const protectedTransportBoundary = new ProtectedTransportBoundary();

export const beginProtectedTransportIdentityTransition = (
  nextUserId: string | null,
): void => {
  protectedTransportBoundary.beginIdentityTransition(nextUserId);
};

export const activateProtectedTransportIdentity = (
  userId: string | null,
  canonicalUserIdReader?: () => string | null,
): void => {
  protectedTransportBoundary.activateIdentity(userId, canonicalUserIdReader);
};

export const cancelProtectedTransportRequests = (): void => {
  protectedTransportBoundary.cancelCurrentIdentityRequests();
};

export const deactivateProtectedTransportBoundary = (): void => {
  protectedTransportBoundary.deactivate();
};

export const captureProtectedTransportBoundary = (): ProtectedTransportSnapshot => (
  protectedTransportBoundary.capture()
);

export const assertProtectedTransportBoundary = (
  snapshot: ProtectedTransportSnapshot,
): void => {
  protectedTransportBoundary.assertCurrent(snapshot);
};

export const rethrowProtectedTransportRequestError = (
  snapshot: ProtectedTransportSnapshot,
  error: unknown,
): never => protectedTransportBoundary.rethrowRequestError(snapshot, error);

export const createProtectedObjectUrl = (
  snapshot: ProtectedTransportSnapshot,
  blob: Blob,
): string => protectedTransportBoundary.createObjectUrl(snapshot, blob);

export const releaseProtectedObjectUrl = (url: string): void => {
  protectedTransportBoundary.releaseObjectUrl(url);
};
